// Wrap a filter as an undoable Command. Captures the layer's original pixel
// data on first apply (lazily) and uses that for invert.
//
// Behaviour:
//   - Finds the target raster layer recursively through groups.
//   - For small images (< 256x256), applies filter synchronously on main thread.
//   - For larger images, offloads to a Web Worker pool for non-blocking processing.
//   - Calls `applyFilterInPlace`, which runs the filter, then writes the
//     result back into the buffer respecting the selection.
//   - Returns the same Document reference: pixel buffers are mutable in this
//     architecture, so a filter's pixel-level mutation does not produce a new
//     immutable tree.
//   - `invert` returns a private `RestorePixelsCommand` that swaps the
//     snapshot back into the same buffer.
//
// Memory (#77): when a small selection is active, only the selection's
// bounding box is snapshotted, not the entire layer. The pixels outside the
// bbox cannot be changed by the filter (because the selection mask gates the
// write-back), so they don't need to be restored on undo. For a 4K layer with
// a 200Ã—200 selection this cuts the snapshot from 48 MB â†’ 160 kB per apply.

import type { Document } from "../document/Document";
import type { Layer, RasterLayer } from "../document/Layer";
import type { Filter } from "../filters/Filter";
import { applyFilterInPlace } from "../filters/Filter";
import type { Selection } from "../selection/Selection";
import type { Command } from "./Command";
import type { ActionStep } from "../actions/ActionStep";

const WORKER_THRESHOLD = 256 * 256; // pixel count below which we use main thread

// Use a bbox-only snapshot when the selection's bbox is smaller than this
// fraction of the full layer area. Above that, the bbox snapshot has too
// little memory benefit to be worth the overhead, so we keep the full clone.
const BBOX_SNAPSHOT_THRESHOLD = 0.25;

interface BBoxRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

let onFilterProgress: ((message: string) => void) | null = null;

export function setFilterProgressCallback(cb: ((message: string) => void) | null): void {
  onFilterProgress = cb;
}

export class ApplyFilterCommand<P> implements Command {
  readonly kind = "apply-filter";
  private snapshot: Uint8ClampedArray | null = null;
  // When non-null, snapshot covers only this bbox within the layer.
  // When null and snapshot is non-null, snapshot covers the full layer.
  private snapshotBBox: BBoxRect | null = null;

  constructor(
    private readonly layerId: string,
    private readonly filter: Filter<P>,
    private readonly params: P,
    private readonly selection: Selection | null,
  ) {}

  apply(doc: Document): Document {
    const layer = findRaster(doc.layers, this.layerId);
    if (!layer) return doc;
    if (this.snapshot === null) {
      const layerW = layer.pixels.width;
      const layerH = layer.pixels.height;
      const layerArea = layerW * layerH;

      // Compute the snapshot bbox: intersect selection bbox with layer
      // bounds (since selection coords are doc-space and layer may be
      // offset). If the selection is too large or absent, fall back to full
      // snapshot.
      const selBbox = this.selection?.bounds();
      if (selBbox && layerArea > 0) {
        // Translate selection bbox into layer-local coords, then clip.
        const lx = Math.max(0, selBbox.x - layer.x);
        const ly = Math.max(0, selBbox.y - layer.y);
        const rx = Math.min(layerW, selBbox.x + selBbox.w - layer.x);
        const ry = Math.min(layerH, selBbox.y + selBbox.h - layer.y);
        const lw = Math.max(0, rx - lx);
        const lh = Math.max(0, ry - ly);
        if (lw > 0 && lh > 0 && lw * lh <= layerArea * BBOX_SNAPSHOT_THRESHOLD) {
          this.snapshotBBox = { x: lx, y: ly, w: lw, h: lh };
          this.snapshot = extractRegion(layer.pixels.data, layerW, this.snapshotBBox);
        }
      }
      // Fallback: snapshot the entire layer buffer.
      if (this.snapshot === null) {
        this.snapshot = new Uint8ClampedArray(layer.pixels.data);
        this.snapshotBBox = null;
      }
    }

    const pixelCount = layer.pixels.width * layer.pixels.height;
    if (pixelCount < WORKER_THRESHOLD) {
      applyFilterInPlace(this.filter, layer.pixels, this.params, this.selection);
      return doc;
    }

    // For large images, apply asynchronously via worker.
    // We still need to return the doc synchronously, so we capture the
    // pre-filter state and schedule the worker work. The snapshot already
    // holds the pre-filter pixels, so undo will work correctly.
    applyFilterAsync(this.filter, layer.pixels, this.params, this.selection);
    return doc;
  }

  invert(_before: Document, _after: Document): Command {
    const snap = this.snapshot;
    if (snap === null) {
      return new NoOpCommand();
    }
    return new RestorePixelsCommand(this.layerId, snap, this.snapshotBBox);
  }


  toActionStep(): ActionStep {
    return {
      id: crypto.randomUUID(),
      name: this.filter.id,
      command: this.filter.id,
      params: this.params as Record<string, unknown>,
      recordable: true,
    };
  }
}

function extractRegion(
  full: Uint8ClampedArray,
  fullWidth: number,
  bbox: BBoxRect,
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Async filter application via Web Workers
// ---------------------------------------------------------------------------

const FILTER_ID_MAP: Record<string, string> = {
  "gaussian-blur": "gaussian-blur",
  "box-blur": "box-blur",
  "motion-blur": "motion-blur",
  "brightness-contrast": "brightness-contrast",
  "invert": "invert",
  "hue-saturation": "hue-saturation",
  "exposure": "exposure",
  "posterize": "posterize",
  "threshold": "threshold",
  "vibrance": "vibrance",
  "color-balance": "color-balance",
};

async function applyFilterAsync<P>(
  filter: Filter<P>,
  buffer: import("../pixel/PixelBuffer").PixelBuffer,
  params: P,
  selection: Selection | null,
): Promise<void> {
  const workerType = FILTER_ID_MAP[filter.id];
  if (!workerType) {
    // Fallback: apply on main thread for unsupported filters
    applyFilterInPlace(filter, buffer, params, selection);
    return;
  }

  if (onFilterProgress) onFilterProgress("Processing filter...");

  try {
    const { getFilterWorkerPool } = await import("../../workers/filterWorkerPool");
    const pool = getFilterWorkerPool();

    const pixels = new Uint8ClampedArray(buffer.data);
    const result = await pool.apply(
      pixels,
      buffer.width,
      buffer.height,
      workerType,
      params as Record<string, number | string | boolean>,
    );

    buffer.data.set(result);
    buffer.markDirty();
  } catch (err) {
    console.error(`Worker filter ${filter.id} failed, falling back to main thread:`, err);
    applyFilterInPlace(filter, buffer, params, selection);
  } finally {
    if (onFilterProgress) onFilterProgress("");
  }
}

// ---------------------------------------------------------------------------
// Private helpers (NOT exported)
// ---------------------------------------------------------------------------

class RestorePixelsCommand implements Command {
  readonly kind = "apply-filter/restore";
  constructor(
    private readonly layerId: string,
    private readonly snapshot: Uint8ClampedArray,
    private readonly snapshotBBox: BBoxRect | null,
  ) {}

  apply(doc: Document): Document {
    const layer = findRaster(doc.layers, this.layerId);
    if (!layer) return doc;
    if (this.snapshotBBox) {
      // Bbox-only snapshot: write the region back into the layer at the
      // stored offset. Validate dimensions match.
      const bb = this.snapshotBBox;
      if (
        bb.x < 0 ||
        bb.y < 0 ||
        bb.x + bb.w > layer.pixels.width ||
        bb.y + bb.h > layer.pixels.height ||
        this.snapshot.length !== bb.w * bb.h * 4
      ) {
        return doc;
      }
      const data = layer.pixels.data;
      for (let row = 0; row < bb.h; row++) {
        const dstOff = ((bb.y + row) * layer.pixels.width + bb.x) * 4;
        const srcOff = row * bb.w * 4;
        data.set(this.snapshot.subarray(srcOff, srcOff + bb.w * 4), dstOff);
      }
      layer.pixels.markDirty();
      return doc;
    }
    // Full-layer snapshot path.
    if (layer.pixels.data.length !== this.snapshot.length) return doc;
    layer.pixels.data.set(this.snapshot);
    layer.pixels.markDirty();
    return doc;
  }

  invert(before: Document, _after: Document): Command {
    const layer = findRaster(before.layers, this.layerId);
    if (!layer) return new NoOpCommand();
    if (this.snapshotBBox) {
      const bb = this.snapshotBBox;
      const snap = new Uint8ClampedArray(bb.w * bb.h * 4);
      const data = layer.pixels.data;
      for (let row = 0; row < bb.h; row++) {
        const srcOff = ((bb.y + row) * layer.pixels.width + bb.x) * 4;
        const dstOff = row * bb.w * 4;
        snap.set(data.subarray(srcOff, srcOff + bb.w * 4), dstOff);
      }
      return new RestorePixelsCommand(this.layerId, snap, bb);
    }
    const snap = new Uint8ClampedArray(layer.pixels.data);
    return new RestorePixelsCommand(this.layerId, snap, null);
  }
}

class NoOpCommand implements Command {
  readonly kind = "apply-filter/noop";
  apply(doc: Document): Document {
    return doc;
  }
  invert(): Command {
    return new NoOpCommand();
  }
}

function findRaster(layers: readonly Layer[], id: string): RasterLayer | null {
  for (const l of layers) {
    if (l.id === id && l.kind === "raster") return l;
    if (l.kind === "group") {
      const inner = findRaster(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}

